import type { BacklogListImport } from "../../../../../_shared/_logic/batch-upload-contract"
import type { WorkOrderCreationFields } from "../../../../../_shared/_logic/work-order-creation"
import type { LoadListTableRow } from "../../../_shared/_logic/load-list-row-model"
import { rowHints, backlogMatchReview } from "../../../_shared/_logic/row-hints"
import type { BacklogEstimate } from "../../../_shared/_logic/backlog-estimation"
import { backlogListSourceNames } from "../../../../../../../../../../../../../../../api/kehrblatt/backlog-lists"

type SourceRow = BacklogListImport["rows"][number]
type SourceField = {
  id: number
  label: string
  value: string
  explanations: string[]
  uncertain: boolean
}
export type RowDetails = {
  estimate?: BacklogEstimate
  currentDuration: string
  manualDuration: boolean
  address: string
  unit: string
  orderTypes: string[]
  selectionOrigin: string
  hints: string[]
  fileName: string
  sourceSystem: string
  sourceNote: string
  sources: Array<{
    id: string
    location: string
    matching: string
    matchWarnings?: string[]
    fields: SourceField[]
  }>
}

const MATCH_REASONS = {
  number_address_conflict:
    "Kundennummer und Adresse widersprechen sich. Liegenschaft und Einheit müssen manuell geprüft werden.",
  number_and_address: "Laufende Nummer und Adresse stimmen mit einer importierten Einheit überein.",
  address: "Die Liegenschaft wurde über die Adresse gefunden.",
  address_unverifiable:
    "Adresse nicht eindeutig lesbar. Die Zuordnung konnte beim Import nicht anhand der Adresse geprüft werden.",
  address_and_location: "Die Liegenschaft wurde über Adresse und Ortsangaben gefunden.",
  unit_number: "Die Kundennummer aus der Liste entspricht der Nummer einer Einheit.",
  import_reference: "Die Kundennummer wurde über den vorhandenen Stammdatenimport zugeordnet.",
  ambiguous: "Mehrere passende Ziele gefunden. Bitte Liegenschaft und Einheit selbst auswählen.",
  not_found: "Kein passendes Ziel gefunden. Bitte Liegenschaft und Einheit selbst auswählen.",
  target_unavailable: "Das gefundene Ziel ist nicht mehr verfügbar. Bitte die Zuordnung prüfen.",
}

const normalizeHeader = (value: string) =>
  value.trim().toLocaleLowerCase("de-DE").replace(/\s+/g, "")
const displayValue = (value: string) =>
  value.replace(
    /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F-\u009F]/g,
    (character) => `\\u${character.charCodeAt(0).toString(16).padStart(4, "0")}`,
  )

const fieldExplanations = (
  column: string,
  source: SourceRow,
  orderTypes: OrderType[],
  backlog: BacklogListImport | null,
) => {
  const evidence = (source.work_evidence ?? []).filter(
    (item) => normalizeHeader(item.column) === normalizeHeader(column),
  )

  if (!evidence.length) {
    if (
      [
        "lnr",
        "kd.-nr.",
        "kdnr.",
        "kundennummer",
        "strasse",
        "straße",
        "liegenschaft",
        "ort",
      ].includes(normalizeHeader(column))
    )
      return ["Angabe für die Zuordnung von Liegenschaft oder Einheit."]

    return ["Nur zur Ansicht; nicht für die Vorauswahl verwendet."]
  }

  return [
    ...new Set(
      evidence.map((item) => {
        const prefix = evidence.length > 1 ? `${item.value}: ` : ""
        const conflict = source.work_warnings?.some(
          (warning) =>
            warning.code === "conflicting_work" &&
            warning.column === item.column &&
            warning.value === item.value,
        )

        if (conflict)
          return `${prefix}Widersprüchliche Angaben; nicht vorgeschlagen. Bitte in der Liste prüfen.`

        if (item.status === "done")
          return `${prefix}Nicht vorgeschlagen: In der Liste als erledigt gekennzeichnet.`

        if (item.status === "empty")
          return "Kein Arbeitsbedarf aus dieser leeren Angabe abgeleitet."

        if (item.status === "unknown")
          return `${prefix}Arbeitsangabe nicht eindeutig verstanden. Bitte Auftragstyp selbst prüfen.`

        if (!item.order_type_key)
          return `${prefix}Arbeit offen; Auftragstyp bitte selbst auswählen.`

        const type = orderTypes.find((candidate) => candidate.key === item.order_type_key)
        const name = type?.internal_label || type?.public_label || "Auftragstyp"
        const reconsideration = item.reconsidered_from_date
          ? `Datum ${item.reconsidered_from_date.split("-").reverse().join(".")} liegt mehr als zwölf Monate zurück. `
          : ""

        if (backlog?.options?.mode === "manual")
          return (
            prefix +
            reconsideration +
            name +
            " wird berücksichtigt. Für die Planung wird Ihre gemeinsame Vorgabe verwendet."
          )

        if (
          backlog?.options?.mode === "automatic" &&
          !backlog.options.order_type_keys.includes(item.order_type_key)
        )
          return prefix + reconsideration + name + " wurde von Ihnen beim Import abgewählt."

        if (reconsideration) return prefix + reconsideration + name + " wird wieder vorgeschlagen."

        return `${prefix}${name} vorgeschlagen: ${normalizeHeader(column) === "begehung" ? "Arbeitskürzel aus der Liste." : "Diese Angabe kennzeichnet offene Arbeiten, auch bei 0,00."}`
      }),
    ),
  ]
}

export const buildRowDetails = ({
  row,
  draft,
  backlog,
  sourcesById,
  orderTypes,
  estimate,
}: {
  row: LoadListTableRow
  draft: {
    orderFields: WorkOrderCreationFields
    copyOriginRowId: string | null
    workSourceCopied?: boolean
    workSourceRowIds?: string[]
    manualOrderTypes?: boolean
    manualDuration?: boolean
    workTypesFromImport?: boolean
    workTargetSelectedManually?: boolean
  }
  backlog: BacklogListImport | null
  sourcesById: Map<string, SourceRow>
  orderTypes: OrderType[]
  estimate?: BacklogEstimate
}): RowDetails => {
  const copied = Boolean(draft.copyOriginRowId || draft.workSourceCopied)
  const sourceIds = copied ? [] : (draft.workSourceRowIds ?? [])
  const sources = sourceIds.flatMap((id) => {
    const source = sourcesById.get(id)

    if (!source) return []

    const review = backlogMatchReview(source, backlog?.sourceSystem, {
      propertyId: row.propertyId,
      selectedUnitId: row.selectedUnitId,
      workTargetSelectedManually: draft.workTargetSelectedManually,
    })
    const matching = source.match_reason
      ? MATCH_REASONS[source.match_reason]
      : "Der genaue Zuordnungsgrund wurde bei diesem Upload noch nicht mitgeliefert."
    const columns = backlog?.sourceColumns ?? []
    const uncertain = new Set(source.source_uncertain_columns ?? [])
    const headers = new Map<string, number>()
    for (const column of columns) headers.set(column, (headers.get(column) ?? 0) + 1)

    return [
      {
        id,
        location: review.location,
        matching: review.appliesToSelection
          ? matching
          : `Ursprüngliche Prüfung, nicht für die aktuelle Auswahl: ${matching}`,
        matchWarnings: review.hints,
        fields: columns.map((column, index) => ({
          id: index,
          label: column.trim()
            ? `${column}${(headers.get(column) ?? 0) > 1 ? ` (Spalte ${index + 1})` : ""}`
            : `Spalte ${index + 1} ohne Überschrift`,
          value:
            source.source_values?.[index] == null
              ? "Nicht mitgeliefert"
              : source.source_values[index] === ""
                ? "Leer"
                : displayValue(source.source_values[index]),
          uncertain: uncertain.has(index),
          explanations: uncertain.has(index)
            ? [
                "Formatierung unklar; unverändert angezeigt. Nicht für die automatische Zuordnung verwendet.",
              ]
            : fieldExplanations(column, source, orderTypes, backlog),
        })),
      },
    ]
  })
  const hints = rowHints(row)
  const selectedTypes = draft.orderFields.order_type_ids.map((id) => {
    const type = orderTypes.find((candidate) => candidate.id === id)

    return type?.internal_label || type?.public_label || "Auftragstyp nicht mehr verfügbar"
  })

  if (draft.orderFields.custom_order_type?.internal_label)
    selectedTypes.push(draft.orderFields.custom_order_type.internal_label)

  return {
    estimate,
    currentDuration: String(draft.orderFields.estimated_duration_min || ""),
    manualDuration: Boolean(draft.manualDuration),
    address: row.address,
    unit:
      row.unitOptions.find((option) => option.value === row.selectedUnitId)?.headline ??
      "Noch keine Einheit ausgewählt",
    orderTypes: selectedTypes,
    selectionOrigin: copied
      ? "Aus einer anderen Planungszeile kopiert. Die Vorlage belegt keine Arbeiten für diese Einheit."
      : draft.manualOrderTypes
        ? "Auftragstypen von Ihnen ausgewählt. Die Angaben unten zeigen weiterhin den Vorschlag aus der Liste."
        : backlog?.options?.mode === "manual" && draft.workTypesFromImport
          ? "Gemeinsame Auftragstypen und Dauer beim Import vorgegeben."
          : draft.workTypesFromImport && selectedTypes.length
            ? "Auftragstypen aus der Liste vorausgewählt."
            : "Für diese Zeile wurden noch keine Auftragstypen aus der Liste übernommen.",
    hints: [
      ...hints.errors,
      ...hints.warnings,
      ...(hints.label === "Wird geprüft" ? ["Offene Aufträge werden gerade geprüft."] : []),
      ...(hints.label === "Noch nicht geprüft"
        ? ["Offene Aufträge wurden noch nicht geprüft."]
        : []),
      ...(draft.workTargetSelectedManually
        ? ["Die aktuelle Einheit wurde von Ihnen ausgewählt."]
        : []),
      ...(row.removed ? ["Diese Zeile ist aus der Planung ausgelassen."] : []),
    ],
    fileName: backlog?.fileName ?? "Dateiname nicht verfügbar",
    sourceSystem: backlog ? backlogListSourceNames[backlog.sourceSystem] : "",
    sourceNote: copied
      ? "Für diese kopierte Zeile ist kein eigener Quelldatensatz zugeordnet."
      : !sources.length
        ? "Dieser Zeile ist keine Quellzeile aus einer Rückstandsliste zugeordnet."
        : sources.some((source) => !source.fields.length)
          ? "Die vollständigen Quellangaben fehlen bei diesem älteren Upload. Bitte die Datei erneut hochladen."
          : "Aus der Datei gelesen",
    sources,
  }
}
